/**
 * Pack loader: JSON on disk to a live world.
 *
 * The loader is the only place that knows how a pack's data maps onto the engine's runtime
 * shapes. Everything downstream — matrix, receiver, UI — sees engine types and never the raw
 * JSON, so the schema can grow without those layers caring.
 *
 * It is also where locale resolution happens, once: strings are picked for the active locale
 * (falling back to the pack default), and audio references become URLs under the locked
 * `audio/<locale>/<npc>/<node>.ogg` convention. Nothing further down the stack handles a locale.
 */
import { DEFAULT_RATIO, DEFAULT_START_GAME_MS } from '../world/clock.js';
import { DEFAULT_TRANSMIT_LIMITS, type TransmitLimits } from '../voice/transmit.js';
import { SignalMatrix, type ScheduledSignal, type SignalSource } from '../world/matrix.js';
import {
  audioAssetPath,
  localized,
  type ContentPack,
  type LocaleTag,
  type PackNpc,
  type PackSignal,
  type PackSignalSource,
} from './schema.js';
import { validatePack, type ValidationIssue } from './validate.js';
import { buildStory, type Story } from '../story/story.js';

export interface LoadedPack {
  id: string;
  title: string;
  locale: LocaleTag;
  defaultLocale: LocaleTag;
  band: { minKhz: number; maxKhz: number };
  clock: { ratio: number; startGameMs: number };
  /** The player's transmitter, in engine units. */
  transmit: TransmitLimits;
  matrix: SignalMatrix;
  /** The story layer, or null for a pack that is only a band (schema v1). */
  story: Story | null;
  npcNames: Map<string, string>;
  /** The authored NPCs, for task resolution and the validator. */
  npcs: PackNpc[];
  /** Non-fatal problems worth surfacing in the console during authoring. */
  warnings: ValidationIssue[];
  raw: ContentPack;
}

export class PackLoadError extends Error {
  constructor(
    message: string,
    readonly issues: ValidationIssue[],
  ) {
    super(message);
    this.name = 'PackLoadError';
  }
}

/** Where a pack's files live, relative to the site root. */
export function packRoot(packId: string): string {
  return `/content/${packId}`;
}

function resolveSource(
  source: PackSignalSource,
  packId: string,
  locale: LocaleTag,
  defaultLocale: LocaleTag,
): SignalSource {
  if (source.type === 'synth') {
    return { type: 'synth', preset: source.preset, params: source.params };
  }

  // Fall back to the default locale's rendering when this locale has not been recorded yet, so a
  // half-finished translation degrades to "the line plays in English" rather than to silence.
  const available = source.locales ?? [locale];
  const chosen = available.includes(locale) ? locale : defaultLocale;
  return {
    type: 'audio',
    url: `${packRoot(packId)}/${audioAssetPath(chosen, source.npc, source.node)}`,
    loop: source.loop ?? false,
  };
}

function toScheduledSignal(
  signal: PackSignal,
  pack: ContentPack,
  locale: LocaleTag,
): ScheduledSignal {
  return {
    id: signal.id,
    kind: signal.kind,
    placement: {
      frequencyKhz: signal.placement.frequencyKhz,
      widthKhz: signal.placement.widthKhz,
      ...(signal.placement.drift ? { drift: signal.placement.drift } : {}),
    },
    schedule: signal.schedule,
    ...(signal.priority !== undefined ? { priority: signal.priority } : {}),
    source: resolveSource(signal.source, pack.id, locale, pack.defaultLocale),
    ...(signal.label ? { label: localized(signal.label, locale, pack.defaultLocale) } : {}),
    ...(signal.caption ? { caption: localized(signal.caption, locale, pack.defaultLocale) } : {}),
    ...(signal.requires ? { requires: signal.requires } : {}),
    ...(signal.effects ? { effects: signal.effects } : {}),
    ...(signal.listenSeconds !== undefined ? { listenSeconds: signal.listenSeconds } : {}),
    ...(signal.dialogue ? { dialogue: signal.dialogue } : {}),
  };
}

/**
 * The pack authors its transmitter in seconds, because that is how a person thinks about holding
 * a key; the engine works in milliseconds. Anything unspecified takes the engine default.
 */
function resolveTransmitLimits(pack: ContentPack): TransmitLimits {
  const transmit = pack.transmit;
  const seconds = (value: number | undefined, fallback: number): number =>
    value === undefined ? fallback : Math.round(value * 1000);

  return {
    maxTransmitMs: seconds(transmit?.maxSeconds, DEFAULT_TRANSMIT_LIMITS.maxTransmitMs),
    cooldownMs: seconds(transmit?.cooldownSeconds, DEFAULT_TRANSMIT_LIMITS.cooldownMs),
    maxConsecutive: transmit?.maxConsecutive ?? DEFAULT_TRANSMIT_LIMITS.maxConsecutive,
    overheatedCooldownMs: seconds(
      transmit?.overheatedSeconds,
      DEFAULT_TRANSMIT_LIMITS.overheatedCooldownMs,
    ),
    consecutiveResetMs: seconds(
      transmit?.burstForgetSeconds,
      DEFAULT_TRANSMIT_LIMITS.consecutiveResetMs,
    ),
  };
}

/**
 * Turn a roaming NPC into a signal.
 *
 * NPCs deliberately become ordinary `ScheduledSignal`s rather than a parallel concept. Everything
 * Chapter 2 built — one transmitter per band, collision detection, priority, the frequency
 * resolver — then applies to them for free, and a player tuning across the dial cannot tell from
 * the mechanics whether they have found a scripted signal, a person, or an NPC. That last part is
 * the whole point of the chapter.
 *
 * Tasks are not folded in here: they depend on the player's story state and the clock, so they are
 * resolved per-moment by `npcTaskStation` rather than baked into a static signal.
 */
function npcToSignal(npc: PackNpc, pack: ContentPack, locale: LocaleTag): ScheduledSignal | null {
  if (!npc.roaming || npc.roaming.stations.length === 0) return null;

  const label =
    localized(npc.label, locale, pack.defaultLocale) ??
    localized(npc.name, locale, pack.defaultLocale);

  return {
    id: `npc:${npc.id}`,
    kind: 'npc',
    placement: {
      // A roaming NPC's frequency comes from its stations; this is the fallback the resolver
      // never reaches, kept sane so a malformed pack degrades rather than producing NaN.
      frequencyKhz: npc.roaming.stations[0]!,
      widthKhz: npc.roaming.widthKhz ?? 45,
      roaming: npc.roaming,
      roamingSeed: npc.id,
    },
    // An NPC's own schedule is "whenever" — its roaming and duty cycle decide when it is heard.
    // Authors constrain it with tasks or by giving the NPC a coverage gate.
    schedule: {},
    priority: 25,
    source: npc.source
      ? resolveSource(npc.source, pack.id, locale, pack.defaultLocale)
      : { type: 'synth', preset: 'voice-carrier', params: { toneHz: 200 } },
    ...(label ? { label } : {}),
    ...(npc.dialogue ? { dialogue: npc.dialogue } : {}),
    npcId: npc.id,
    ...(npc.coverage ? { coverage: npc.coverage } : {}),
  };
}

/** Turn a validated pack into the runtime world. */
export function buildPack(
  pack: ContentPack,
  locale: LocaleTag,
  warnings: ValidationIssue[] = [],
): LoadedPack {
  const activeLocale = pack.locales.includes(locale) ? locale : pack.defaultLocale;

  const npcNames = new Map<string, string>();
  for (const npc of pack.npcs ?? []) {
    const name = localized(npc.name, activeLocale, pack.defaultLocale);
    if (name) npcNames.set(npc.id, name);
  }

  return {
    id: pack.id,
    title: localized(pack.title, activeLocale, pack.defaultLocale) ?? pack.id,
    locale: activeLocale,
    defaultLocale: pack.defaultLocale,
    band: pack.band,
    clock: {
      ratio: pack.clock?.ratio ?? DEFAULT_RATIO,
      startGameMs: pack.clock?.startGameMs ?? DEFAULT_START_GAME_MS,
    },
    transmit: resolveTransmitLimits(pack),
    matrix: new SignalMatrix([
      ...pack.signals.map((signal) => toScheduledSignal(signal, pack, activeLocale)),
      // NPCs join the band as ordinary transmitters.
      ...(pack.npcs ?? [])
        .map((npc) => npcToSignal(npc, pack, activeLocale))
        .filter((signal): signal is ScheduledSignal => signal !== null),
    ]),
    // The loader is the only layer that knows the pack root and the active locale, so it is the
    // only place a dialogue node's pre-rendered reading can be turned into a URL.
    story: buildStory(pack, activeLocale, pack.defaultLocale, (source) => {
      const resolved = resolveSource(source, pack.id, activeLocale, pack.defaultLocale);
      return resolved.type === 'audio' ? resolved.url : null;
    }),
    npcNames,
    npcs: pack.npcs ?? [],
    warnings,
    raw: pack,
  };
}

/** Validate raw JSON and build it, or throw with every problem listed. */
export function loadPackFromJson(input: unknown, locale: LocaleTag): LoadedPack {
  const result = validatePack(input);
  if (!result.ok || !result.pack) {
    const summary = result.errors
      .slice(0, 5)
      .map((issue) => `  ${issue.path || '(root)'}: ${issue.message}`)
      .join('\n');
    throw new PackLoadError(
      `content pack failed validation (${result.errors.length} error(s)):\n${summary}`,
      result.errors,
    );
  }
  return buildPack(result.pack, locale, result.warnings);
}

/** Fetch and load a pack by id. */
export async function fetchPack(packId: string, locale: LocaleTag): Promise<LoadedPack> {
  const url = `${packRoot(packId)}/pack.json`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new PackLoadError(`could not fetch content pack at ${url} (HTTP ${response.status})`, []);
  }
  return loadPackFromJson(await response.json(), locale);
}
